import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { appLocales } from 'helpers/constants/appLocales';
import { useLocaleContext } from 'helpers/contexts/LocaleContext';
import { detectLocaleFromHost } from 'helpers/utils/localeDetection';
import { Locales } from 'helpers/utils/swiftType';
import { TLocalizationInfo } from 'types/TLocalizationInfo';
import { getLocalizationInfo } from '../../project.config';

interface Input {
  darkList?: string[];
  whiteList?: string[];
}

export const useLocale = ({ darkList, whiteList }: Input = {}) => {
  const router = useRouter();
  const { serverLocale } = useLocaleContext();

  // Prioritize locale from server-side context (x-locale header) over other methods
  const getActiveLocale = () => {
    // 1. First try server locale from context (passed from pageProps.locale via x-locale header)
    if (serverLocale) {
      return serverLocale;
    }

    // 2. Fallback to host-based detection for client-side consistency
    if (typeof window !== 'undefined') {
      const host = window.location.host;
      const hostLocale = detectLocaleFromHost(host);

      // Only fall back to router.locale if host detection returns the default 'en-US'
      // and router has a different locale
      if (hostLocale !== 'en-US' || !router?.locale) {
        return hostLocale;
      }
    }

    // 3. Final fallback to router.locale for SSR or when other methods fail
    return router.locale || 'en-US';
  };

  const currentLocale = getActiveLocale();
  const localizationInfo: TLocalizationInfo = getLocalizationInfo(currentLocale);
  const locale = localizationInfo.appLocale as Locales;
  const isUsaStore = locale === appLocales.EN_US;
  const isCanadaStore = locale === appLocales.EN_CA || locale === appLocales.FR_CA;
  const isMexicoStore = locale === appLocales.ES_MX;
  const isLatamStore = [appLocales.ES_MX, appLocales.PT_BR, appLocales.ES_LATAM].includes(locale);

  let defaultAuthorized = true;
  if (darkList) defaultAuthorized = true;
  if (whiteList) defaultAuthorized = false;
  const [authorized, setAuthorized] = useState(defaultAuthorized);

  useEffect(() => {
    if (darkList?.includes(locale)) {
      setAuthorized(false);
    } else if (whiteList?.includes(locale)) {
      setAuthorized(true);
    }
  }, [darkList, locale, whiteList]);

  return { locale, authorized, isCanadaStore, isUsaStore, isMexicoStore, localizationInfo, isLatamStore };
};
